import React from "react";
import {
  Box,
  Typography,
  Grid,
  ThemeProvider,
  createTheme,
  Container,
  Button,
} from "@mui/material";
import { useNavigate } from "react-router-dom";

import CheckroomOutlinedIcon from "@mui/icons-material/CheckroomOutlined";
import EnergySavingsLeafOutlinedIcon from "@mui/icons-material/EnergySavingsLeafOutlined";
import GridViewOutlinedIcon from "@mui/icons-material/GridViewOutlined";
import VerifiedOutlinedIcon from "@mui/icons-material/VerifiedOutlined";
import EventAvailableOutlinedIcon from "@mui/icons-material/EventAvailableOutlined";
import SupportAgentOutlinedIcon from "@mui/icons-material/SupportAgentOutlined";
import aboutus2 from "../../Assets/aboutus2.png";
import aboutbaner from "../../Assets/aboutbaner.png";

import Navbar from "../Navbar/Navbar";
import Footer from "../Footer/Footer";

const theme = createTheme({
  palette: {
    mode: "light",
    primary: {
      main: "#111111",
    },
    background: {
      default: "#ffffff",
      paper: "#ffffff",
    },
  },

  typography: {
    fontFamily: '"Poppins", "Helvetica Neue", Arial, sans-serif',

    h1: {
      fontWeight: 400,
      letterSpacing: "-0.045em",
    },

    h2: {
      fontWeight: 400,
      letterSpacing: "-0.035em",
    },

    body1: {
      lineHeight: 1.8,
    },

    body2: {
      lineHeight: 1.7,
    },
  },

  shape: {
    borderRadius: 0,
  },
});

const benefits = [
  {
    number: "01",
    title: "Affordable Fashion",
    description:
      "Experience premium styles without the commitment of buying every outfit.",
    icon: CheckroomOutlinedIcon,
  },
  {
    number: "02",
    title: "Eco-Friendly",
    description:
      "Give fashion a longer life and make a more conscious choice with every rental.",
    icon: EnergySavingsLeafOutlinedIcon,
  },
  {
    number: "03",
    title: "Wide Variety",
    description:
      "Discover looks for weddings, celebrations, parties, shoots and special occasions.",
    icon: GridViewOutlinedIcon,
  },
  {
    number: "04",
    title: "Quality Assurance",
    description:
      "Every piece is carefully selected and prepared so you can wear it with confidence.",
    icon: VerifiedOutlinedIcon,
  },
  {
    number: "05",
    title: "Convenient Rental",
    description:
      "Choose your look, enjoy your occasion and return it when the moment is over.",
    icon: EventAvailableOutlinedIcon,
  },
  {
    number: "06",
    title: "Customer Support",
    description:
      "Our team is here to make your rental experience simple, smooth and memorable.",
    icon: SupportAgentOutlinedIcon,
  },
];

export default function AboutUs() {
  const navigate = useNavigate();

  const handleExplore = () => {
    navigate("/women");

    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  };

  return (
    <ThemeProvider theme={theme}>
      <Box
        sx={{
          width: "100%",
          bgcolor: "#fff",
          color: "#111",
          overflow: "hidden",
        }}
      >
        <Navbar />

        {/* ================= HERO  ================= */}
        <Box
          sx={{
            position: "relative",
            width: "100%",
            height: {
              xs: 300,
              sm: 390,
              md: 500,
            },
            overflow: "hidden",
            bgcolor: "#191919",
          }}
        >
          <Box
            component="img"
            src={aboutbaner}
            alt="Hindustan Wardrobe"
            sx={{
              position: "absolute",
              inset: 0,
              width: "100%",
              height: "100%",
              objectFit: "cover",
              objectPosition: "center",
              display: "block",
            }}
          />

          <Box
            sx={{
              position: "absolute",
              inset: 0,
              background:
                "linear-gradient(180deg, rgba(0,0,0,0.05) 0%, rgba(0,0,0,0.12) 45%, rgba(0,0,0,0.65) 100%)",
            }}
          />

          <Container
            maxWidth="lg"
            sx={{
              position: "relative",
              height: "100%",
              zIndex: 2,
              display: "flex",
              alignItems: "flex-end",
              pb: {
                xs: 4,
                sm: 5,
                md: 6,
              },
            }}
          >
            <Box>
              <Typography
                sx={{
                  color: "rgba(255,255,255,0.75)",
                  fontSize: {
                    xs: 8,
                    sm: 9,
                  },
                  fontWeight: 600,
                  letterSpacing: "0.3em",
                  textTransform: "uppercase",
                  mb: 1.2,
                }}
              >
                Hindustan Wardrobe
              </Typography>

              <Typography
                component="h1"
                sx={{
                  color: "#fff",
                  fontSize: {
                    xs: 32,
                    sm: 44,
                    md: 55,
                  },
                  lineHeight: 1,
                  fontWeight: 400,
                  letterSpacing: "-0.045em",
                }}
              >
                About Us
              </Typography>
            </Box>
          </Container>
        </Box>

        {/* ================= ABOUT STORY ================= */}
        <Container
          maxWidth="lg"
          sx={{
            py: {
              xs: 7,
              sm: 9,
              md: 12,
            },
          }}
        >
          <Grid
            container
            spacing={{
              xs: 5,
              md: 10,
            }}
            alignItems="center"
          >
            <Grid
              item
              xs={12}
              md={5}
            >
              <Typography
                sx={{
                  color: "#888",
                  fontSize: 8,
                  fontWeight: 600,
                  letterSpacing: "0.3em",
                  textTransform: "uppercase",
                  mb: 2,
                }}
              >
                Our Story
              </Typography>

              <Typography
                component="h2"
                sx={{
                  fontSize: {
                    xs: 34,
                    sm: 42,
                    md: 50,
                  },
                  lineHeight: 1.05,
                  letterSpacing: "-0.045em",
                }}
              >
                A wardrobe for
                <Box
                  component="span"
                  sx={{
                    display: "block",
                    fontStyle: "italic",
                  }}
                >
                  every occasion.
                </Box>
              </Typography>
            </Grid>

            <Grid
              item
              xs={12}
              md={7}
            >
              <Typography
                sx={{
                  maxWidth: 650,
                  color: "#555",
                  fontSize: {
                    xs: 13,
                    sm: 14,
                    md: 15,
                  },
                  lineHeight: 1.9,
                }}
              >
                Hindustan Wardrobe is changing the way people experience
                fashion. Instead of buying an outfit for a single
                occasion, we believe you should have the freedom to
                discover beautiful styles, wear them for your special
                moments, and return them when the occasion is over.
              </Typography>

              <Typography
                sx={{
                  maxWidth: 650,
                  mt: 2.5,
                  color: "#555",
                  fontSize: {
                    xs: 13,
                    sm: 14,
                    md: 15,
                  },
                  lineHeight: 1.9,
                }}
              >
                From weddings and celebrations to parties, shoots and
                unforgettable evenings, our collection brings together
                fashion that lets you express your individuality without
                the commitment of owning every look.
              </Typography>

              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1.5,
                  mt: 3.5,
                }}
              >
                <Box
                  sx={{
                    width: 40,
                    height: "1px",
                    bgcolor: "#111",
                  }}
                />

                <Typography
                  sx={{
                    color: "#777",
                    fontSize: 8,
                    letterSpacing: "0.2em",
                    textTransform: "uppercase",
                  }}
                >
                  Rent · Wear · Return
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </Container>

        {/* ================= PHILOSOPHY ================= */}
        <Box
          sx={{
            bgcolor: "#f6f5f2",
            py: {
              xs: 7,
              sm: 9,
              md: 11,
            },
          }}
        >
          <Container maxWidth="md">
            <Box sx={{ textAlign: "center" }}>
              <Typography
                sx={{
                  color: "#888",
                  fontSize: 8,
                  fontWeight: 600,
                  letterSpacing: "0.3em",
                  textTransform: "uppercase",
                  mb: 2.5,
                }}
              >
                Our Philosophy
              </Typography>

              <Typography
                component="h2"
                sx={{
                  fontSize: {
                    xs: 28,
                    sm: 36,
                    md: 44,
                  },
                  lineHeight: 1.2,
                  letterSpacing: "-0.04em",
                }}
              >
                Fashion should be about the
                <Box
                  component="span"
                  sx={{
                    fontStyle: "italic",
                  }}
                >
                  {" "}
                  moment,
                </Box>
                <br />
                not the wardrobe.
              </Typography>

              <Typography
                sx={{
                  maxWidth: 590,
                  mx: "auto",
                  mt: 2.5,
                  color: "#777",
                  fontSize: 12,
                  lineHeight: 1.85,
                }}
              >
                At Hindustan Wardrobe, we are creating a fashion experience
                where style is accessible, occasions are celebrated, and
                clothing can be enjoyed again and again.
              </Typography>
            </Box>
          </Container>
        </Box>

        {/* ================= IMAGE ================= */}
        <Container
          maxWidth="lg"
          sx={{
            py: {
              xs: 7,
              sm: 9,
              md: 12,
            },
          }}
        >
          <Box
            sx={{
              position: "relative",
              width: "100%",
              aspectRatio: {
                xs: "4 / 3",
                sm: "16 / 9",
              },
              overflow: "hidden",
              bgcolor: "#e8e6e2",
            }}
          >
            <Box
              component="img"
              src={aboutus2}
              alt="Hindustan Wardrobe collection"
              sx={{
                position: "absolute",
                inset: 0,
                width: "100%",
                height: "100%",
                objectFit: "cover",
                objectPosition: "center",
                display: "block",
              }}
            />
          </Box>
        </Container>

        {/* ================= CTA ================= */}
        <Box
          sx={{
            borderTop: "1px solid #e6e6e6",
            borderBottom: "1px solid #e6e6e6",
            py: {
              xs: 7,
              sm: 9,
              md: 11,
            },
          }}
        >
          <Container maxWidth="md">
            <Box sx={{ textAlign: "center" }}>
              <Typography
                component="h2"
                sx={{
                  fontSize: {
                    xs: 29,
                    sm: 38,
                    md: 48,
                  },
                  lineHeight: 1.1,
                  letterSpacing: "-0.04em",
                }}
              >
                Join us in
                <Box
                  component="span"
                  sx={{
                    fontStyle: "italic",
                  }}
                >
                  {" "}
                  revolutionizing fashion.
                </Box>
              </Typography>

              <Typography
                sx={{
                  maxWidth: 520,
                  mx: "auto",
                  mt: 2,
                  color: "#777",
                  fontSize: 12,
                  lineHeight: 1.8,
                }}
              >
                Your next unforgettable look is waiting. Explore our
                collection and find something made for your moment.
              </Typography>

              <Button
                onClick={handleExplore}
                variant="contained"
                disableElevation
                sx={{
                  mt: 3.5,
                  px: 4,
                  py: 1.5,
                  bgcolor: "#111",
                  color: "#fff",
                  borderRadius: 0,
                  fontSize: 8.5,
                  fontWeight: 600,
                  letterSpacing: "0.2em",
                  textTransform: "uppercase",
                  "&:hover": {
                    bgcolor: "#333",
                  },
                }}
              >
                Explore Fashion
              </Button>
            </Box>
          </Container>
        </Box>

        {/* ================= WHY CHOOSE US ================= */}
        <Box
          sx={{
            bgcolor: "#f6f5f2",
            py: {
              xs: 7,
              sm: 9,
              md: 12,
            },
          }}
        >
          <Container maxWidth="lg">
            <Box
              sx={{
                mb: {
                  xs: 5,
                  md: 7,
                },
              }}
            >
              <Typography
                sx={{
                  color: "#888",
                  fontSize: 8,
                  fontWeight: 600,
                  letterSpacing: "0.3em",
                  textTransform: "uppercase",
                  mb: 1.8,
                }}
              >
                Why Choose Us
              </Typography>

              <Typography
                component="h2"
                sx={{
                  fontSize: {
                    xs: 32,
                    sm: 40,
                    md: 48,
                  },
                  lineHeight: 1.05,
                  letterSpacing: "-0.04em",
                }}
              >
                More than a rental.
              </Typography>

              <Typography
                sx={{
                  mt: 0.8,
                  color: "#666",
                  fontSize: {
                    xs: 23,
                    sm: 28,
                    md: 32,
                  },
                  fontStyle: "italic",
                  lineHeight: 1.15,
                }}
              >
                It's an experience.
              </Typography>
            </Box>

            <Grid
              container
              spacing={2}
            >
              {benefits.map((item) => {
                const ItemIcon = item.icon;

                return (
                  <Grid
                    item
                    xs={12}
                    sm={6}
                    md={4}
                    key={item.title}
                  >
                    <Box
                      sx={{
                        height: "100%",
                        minHeight: {
                          xs: 205,
                          sm: 225,
                        },
                        p: {
                          xs: 3,
                          sm: 3.5,
                        },
                        bgcolor: "#fff",
                        border: "1px solid rgba(0,0,0,0.06)",
                        display: "flex",
                        flexDirection: "column",
                        justifyContent: "space-between",
                        transition:
                          "transform 0.35s ease, box-shadow 0.35s ease",
                        "&:hover": {
                          transform: "translateY(-5px)",
                          boxShadow: "0 18px 45px rgba(0,0,0,0.07)",
                        },
                      }}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                        }}
                      >
                        <Typography
                          sx={{
                            color: "#999",
                            fontSize: 8,
                            letterSpacing: "0.18em",
                          }}
                        >
                          {item.number}
                        </Typography>

                        <Box
                          sx={{
                            width: 42,
                            height: 42,
                            borderRadius: "50%",
                            bgcolor: "#f4f3f0",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                          }}
                        >
                          <ItemIcon
                            sx={{
                              fontSize: 21,
                              color: "#222",
                            }}
                          />
                        </Box>
                      </Box>

                      <Box sx={{ mt: 4 }}>
                        <Typography
                          sx={{
                            color: "#111",
                            fontSize: {
                              xs: 16,
                              sm: 17,
                            },
                            fontWeight: 500,
                            mb: 1,
                          }}
                        >
                          {item.title}
                        </Typography>

                        <Typography
                          sx={{
                            color: "#777",
                            fontSize: 10.5,
                            lineHeight: 1.8,
                          }}
                        >
                          {item.description}
                        </Typography>
                      </Box>
                    </Box>
                  </Grid>
                );
              })}
            </Grid>
          </Container>
        </Box>

        <Box
          sx={{
            bgcolor: "#151515",
            color: "#fff",
            py: {
              xs: 7,
              sm: 9,
              md: 11,
            },
          }}
        >
          <Container maxWidth="md">
            <Box sx={{ textAlign: "center" }}>
              <Typography
                sx={{
                  color: "rgba(255,255,255,0.42)",
                  fontSize: 8,
                  fontWeight: 600,
                  letterSpacing: "0.3em",
                  textTransform: "uppercase",
                  mb: 2.5,
                }}
              >
                Hindustan Wardrobe
              </Typography>

              <Typography
                sx={{
                  fontSize: {
                    xs: 32,
                    sm: 42,
                    md: 54,
                  },
                  lineHeight: 1.08,
                  letterSpacing: "-0.045em",
                }}
              >
                Wear something
                <Box
                  component="span"
                  sx={{
                    display: "block",
                    fontStyle: "italic",
                  }}
                >
                  unforgettable.
                </Box>
              </Typography>

              <Typography
                sx={{
                  mt: 2.2,
                  color: "rgba(255,255,255,0.55)",
                  fontSize: 11.5,
                  lineHeight: 1.8,
                }}
              >
                Choose your outfit. Enjoy the moment. Return it when the
                occasion is over.
              </Typography>

              <Button
                onClick={handleExplore}
                variant="outlined"
                sx={{
                  mt: 3.5,
                  px: 4,
                  py: 1.5,
                  borderRadius: 0,
                  borderColor: "rgba(255,255,255,0.35)",
                  color: "#fff",
                  fontSize: 8.5,
                  fontWeight: 600,
                  letterSpacing: "0.2em",
                  textTransform: "uppercase",
                  "&:hover": {
                    borderColor: "#fff",
                    bgcolor: "rgba(255,255,255,0.06)",
                  },
                }}
              >
                Explore Fashion
              </Button>
            </Box>
          </Container>
        </Box>

        <Footer />
      </Box>
    </ThemeProvider>
  );
}